/**
 * Mini Calendar Widget
 *
 * Compact monthly calendar for dashboard and sidebar use.
 * Shows event indicators and navigates to full calendar on click.
 *
 * @module shared/components/widgets/MiniCalendarWidget
 */

import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameMonth,
  isToday,
  addMonths,
  subMonths,
} from 'date-fns';
import { bg } from 'date-fns/locale';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@/shared/components/ui/card';
import { useAppNavigation } from '@/shared/hooks/useAppNavigation';
import { toLocalDateKey } from '@/shared/lib/dateKeys';
import { cn } from '@/shared/lib/utils';
import { ButtonVariant, ButtonSize } from '@/shared/types/button.types';

// ============================================
// Types
// ============================================

type WidgetSize = 'sm' | 'md';

/** Minimal event shape consumed by the mini calendar (date indicator only). */
interface MiniCalendarEvent {
  readonly startDate: string;
}

interface MiniCalendarWidgetProps {
  /** Events used to render day indicators */
  readonly events?: readonly MiniCalendarEvent[];
  /** Widget size variant */
  readonly size?: WidgetSize;
  /** Additional CSS classes */
  readonly className?: string;
  /** Show header with navigation */
  readonly showHeader?: boolean;
  /** Initial date to display */
  readonly initialDate?: Date;
}

// ============================================
// Size Configuration
// ============================================

const SIZE_CONFIG: Record<
  WidgetSize,
  {
    cellSize: string;
    headerText: string;
    dayText: string;
    gap: string;
  }
> = {
  sm: {
    cellSize: 'h-6 w-6',
    headerText: 'text-xs',
    dayText: 'text-xs',
    gap: 'gap-0.5',
  },
  md: {
    cellSize: 'h-8 w-8',
    headerText: 'text-sm',
    dayText: 'text-sm',
    gap: 'gap-1',
  },
};

// ============================================
// Component
// ============================================

export function MiniCalendarWidget({
  events = [],
  size = 'md',
  className,
  showHeader = true,
  initialDate,
}: MiniCalendarWidgetProps) {
  const { goToCalendar } = useAppNavigation();

  // Current viewed month
  const [viewDate, setViewDate] = useState(initialDate ?? new Date());

  // Get size config
  const config = SIZE_CONFIG[size];

  // Build event map for the current month
  const eventDates = useMemo(() => {
    const dates = new Set<string>();
    for (const event of events) {
      // Add event start date
      dates.add(toLocalDateKey(new Date(event.startDate)));
      // For multi-day events, we could add all dates in range
      // but for mini widget, just start date is enough
    }
    return dates;
  }, [events]);

  // Calculate calendar grid
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(viewDate);
    const monthEnd = endOfMonth(viewDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 }); // Monday start
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [viewDate]);

  // Navigation handlers
  const handlePrevMonth = () => setViewDate(subMonths(viewDate, 1));
  const handleNextMonth = () => setViewDate(addMonths(viewDate, 1));
  const handleToday = () => setViewDate(new Date());

  // Click on a day navigates to calendar
  const handleDayClick = (day: Date) => {
    const dateKey = toLocalDateKey(day);
    goToCalendar({ date: dateKey });
  };

  // Click on header navigates to calendar
  const handleHeaderClick = () => {
    goToCalendar();
  };

  // Week day headers
  const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];

  return (
    <Card className={cn('overflow-hidden', className)}>
      {showHeader && (
        <CardHeader className="px-3 pb-2 pt-3">
          <div className="flex items-center justify-between">
            <CardTitle
              className={cn(
                'flex items-center gap-1.5 cursor-pointer hover:text-primary transition-colors',
                config.headerText
              )}
              onClick={handleHeaderClick}
            >
              <CalendarIcon className="size-4" />
              <span className="capitalize">
                {format(viewDate, 'LLLL yyyy', { locale: bg })}
              </span>
            </CardTitle>

            <div className="flex items-center gap-0.5">
              <Button
                variant={ButtonVariant.Ghost}
                size={ButtonSize.Icon}
                className="size-6"
                onClick={handlePrevMonth}
                aria-label="Предишен месец"
              >
                <ChevronLeft className="size-3.5" />
              </Button>
              <Button
                variant={ButtonVariant.Ghost}
                size={ButtonSize.Icon}
                className="size-6"
                onClick={handleToday}
                aria-label="Днес"
              >
                <span className="text-xs font-medium">●</span>
              </Button>
              <Button
                variant={ButtonVariant.Ghost}
                size={ButtonSize.Icon}
                className="size-6"
                onClick={handleNextMonth}
                aria-label="Следващ месец"
              >
                <ChevronRight className="size-3.5" />
              </Button>
            </div>
          </div>
        </CardHeader>
      )}

      <CardContent className={cn('p-3', showHeader && 'pt-0')}>
        {/* Horizontal scroll wrapper for narrow screens */}
        <div className="-mx-3 overflow-x-auto px-3 sm:mx-0 sm:overflow-x-visible sm:px-0">
          <div className="min-w-[200px] sm:min-w-0">
            {/* Week day headers */}
            <div className={cn('grid grid-cols-7 mb-1', config.gap)}>
              {weekDays.map((day) => (
                <div
                  key={day}
                  className={cn(
                    'flex items-center justify-center text-muted-foreground font-medium',
                    config.cellSize,
                    config.dayText
                  )}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className={cn('grid grid-cols-7', config.gap)}>
              {calendarDays.map((day) => {
                const dateKey = toLocalDateKey(day);
                const hasEvents = eventDates.has(dateKey);
                const isCurrentMonth = isSameMonth(day, viewDate);
                const isCurrentDay = isToday(day);

                return (
                  <button
                    key={dateKey}
                    type="button"
                    onClick={() => handleDayClick(day)}
                    className={cn(
                      'flex flex-col items-center justify-center rounded-md transition-colors relative',
                      config.cellSize,
                      config.dayText,
                      // Base styles
                      'hover:bg-accent hover:text-accent-foreground',
                      // Muted for other months
                      !isCurrentMonth && 'text-muted-foreground/50',
                      // Today highlight
                      isCurrentDay &&
                        'bg-primary text-primary-foreground font-semibold',
                      // Focus styles
                      'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1'
                    )}
                    aria-label={format(day, 'PPP', { locale: bg })}
                  >
                    <span>{format(day, 'd')}</span>
                    {/* Event indicator dot */}
                    {hasEvents && !isCurrentDay && (
                      <span
                        className="absolute bottom-0.5 size-1 rounded-full bg-primary"
                        aria-hidden="true"
                      />
                    )}
                    {hasEvents && isCurrentDay && (
                      <span
                        className="absolute bottom-0.5 size-1 rounded-full bg-primary-foreground"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Default export for easier imports
export default MiniCalendarWidget;
